import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Trash2, Eye, Package } from "lucide-react";

const STATUS = {
  new: { label: "Yeni", cls: "bg-[#d4af37]/10 text-[#d4af37]" },
  processing: { label: "Hazırlanıyor", cls: "bg-blue-500/10 text-blue-300" },
  shipped: { label: "Kargoya verildi", cls: "bg-purple-500/10 text-purple-300" },
  completed: { label: "Tamamlandı", cls: "bg-green-500/10 text-green-300" },
  cancelled: { label: "İptal", cls: "bg-red-500/10 text-red-300" },
};

function fmt(n) {
  return new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 2 }).format(n || 0);
}

export default function AdminOrders() {
  const [items, setItems] = useState([]);
  const [view, setView] = useState(null);

  const load = () => api.get("/orders").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setStatus = async (o, status) => {
    try {
      await api.put(`/orders/${o.id}/status`, null, { params: { status } });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (o) => {
    if (!window.confirm("Sipariş silinsin mi?")) return;
    try {
      await api.delete(`/orders/${o.id}`);
      toast.success("Silindi");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div>
      <div className="wb-overline">Yönetim</div>
      <h1 className="wb-h2 mt-2">Siparişler</h1>

      <div className="wb-card mt-8 overflow-x-auto">
        <table className="w-full min-w-[820px] text-sm">
          <thead>
            <tr className="border-b border-white/10 text-left text-[#a0aabf] text-xs uppercase tracking-widest">
              <th className="p-4">Sipariş</th>
              <th className="p-4">Müşteri</th>
              <th className="p-4">Tip</th>
              <th className="p-4">Toplam</th>
              <th className="p-4">Durum</th>
              <th className="p-4">Tarih</th>
              <th className="p-4 text-right">İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan="7" className="p-8 text-center text-[#a0aabf]">Henüz sipariş yok.</td></tr>
            ) : items.map((o) => (
              <tr key={o.id} className="border-b border-white/5" data-testid={`row-order-${o.id}`}>
                <td className="p-4">
                  <div className="font-mono text-xs text-[#d4af37]">#{o.id.slice(0, 8)}</div>
                  <div className="text-xs text-[#a0aabf] mt-1">{o.items?.length || 0} kalem</div>
                </td>
                <td className="p-4">
                  <div>{o.name}</div>
                  <div className="text-xs text-[#a0aabf]">{o.email}</div>
                </td>
                <td className="p-4 text-xs">
                  <div>{o.customer_type === "corporate" ? "Kurumsal" : "Bireysel"}</div>
                  {o.is_dealer && <div className="text-[#d4af37] mt-1">Bayi · %{o.dealer_discount_percent}</div>}
                </td>
                <td className="p-4 font-serif text-[#d4af37]">{fmt(o.total)}</td>
                <td className="p-4">
                  <select value={o.status} onChange={(e) => setStatus(o, e.target.value)}
                    className="bg-[#030f26] border border-white/10 text-xs px-2 py-1">
                    {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </td>
                <td className="p-4 text-[#a0aabf] text-xs">{new Date(o.created_at).toLocaleString("tr-TR")}</td>
                <td className="p-4 text-right space-x-2">
                  <button onClick={() => setView(o)} className="p-2 border border-white/10 hover:border-[#d4af37] hover:text-[#d4af37]"><Eye size={14} /></button>
                  <button onClick={() => remove(o)} className="p-2 border border-white/10 hover:border-red-500 hover:text-red-400"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {view && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={() => setView(null)}>
          <div className="bg-[#081b3d] border border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-white/10 flex items-start justify-between">
              <div>
                <h2 className="font-serif text-xl">Sipariş #{view.id.slice(0, 8)}</h2>
                <div className="text-xs text-[#a0aabf] mt-1">{new Date(view.created_at).toLocaleString("tr-TR")}</div>
              </div>
              <span className={`text-xs px-2 py-1 ${STATUS[view.status]?.cls || ""}`}>{STATUS[view.status]?.label || view.status}</span>
            </div>
            <div className="p-6 space-y-6 text-sm">
              <div>
                <div className="wb-overline mb-2">Müşteri</div>
                <div>{view.name}</div>
                <div className="text-[#a0aabf]">{view.email} · {view.phone}</div>
                <div className="text-[#a0aabf] mt-1">{view.customer_type === "corporate" ? "Kurumsal" : "Bireysel"}{view.is_dealer && <span className="text-[#d4af37]"> · Bayi (%{view.dealer_discount_percent})</span>}</div>
              </div>
              {view.customer_type === "corporate" && (
                <div>
                  <div className="wb-overline mb-2">Fatura</div>
                  <div>{view.company}</div>
                  <div className="text-[#a0aabf]">Vergi No: {view.tax_id}</div>
                  <div className="text-[#a0aabf]">Vergi Dairesi: {view.tax_office}</div>
                </div>
              )}
              <div>
                <div className="wb-overline mb-2">Teslimat</div>
                <div>{view.city}</div>
                <div className="text-[#a0aabf] whitespace-pre-line mt-1">{view.address}</div>
                {view.notes && <div className="text-[#a0aabf] mt-2"><strong>Not:</strong> {view.notes}</div>}
              </div>
              <div>
                <div className="wb-overline mb-3">Ürünler</div>
                <div className="border border-white/10">
                  {view.items?.map((it, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 border-b border-white/5 last:border-0">
                      <Package size={16} className="text-[#d4af37]" />
                      <div className="flex-1 min-w-0">
                        <div className="truncate">{it.name}</div>
                        <div className="text-xs text-[#a0aabf]">{it.volume} · {it.quantity} × {fmt(it.unit_price)}</div>
                      </div>
                      <div className="text-right text-[#d4af37]">{fmt(it.line_total)}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="border-t border-white/10 pt-4 flex justify-between font-serif text-xl">
                <span>Toplam</span>
                <span className="text-[#d4af37]">{fmt(view.total)}</span>
              </div>
              <div className="text-xs text-[#a0aabf]">KDV %20 dahildir{view.is_dealer && " · Bayi siparişi: kargo alıcıya aittir"}</div>
            </div>
            <div className="p-6 border-t border-white/10 flex justify-end gap-3">
              <a href={`mailto:${view.email}?subject=Sipariş #${view.id.slice(0,8)}`} className="wb-btn-secondary">E-posta Gönder</a>
              <button onClick={() => setView(null)} className="wb-btn-primary">Kapat</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
